import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { ArrowUpRight, CalendarRange, Clock, Hash, Newspaper } from "lucide-react";
import { api, type Forum } from "@/lib/api";
import { CategoryBadge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { RefreshAllButton } from "@/components/RefreshAllButton";
import { PriceTag } from "@/components/PriceTag";
import { TrendBadge } from "@/components/TrendBadge";
import { ForumNews } from "@/components/ForumNews";
import { SummarizeButton } from "@/components/SummarizeButton";
import { useSeo } from "@/lib/useSeo";
import { ForumHistory } from "@/components/ForumHistory";
import { ForumThreadsTable } from "@/components/ForumThreadsTable";
import { usePathTab } from "@/lib/usePathTab";
import { cn, formatNumber, formatScore, fromNowFR } from "@/lib/utils";

const FORUM_TABS = ["threads", "news", "history"] as const;
type ForumTab = (typeof FORUM_TABS)[number];

export default function ThreadsPage() {
  const { id } = useParams();
  const [tab, setTab] = usePathTab<ForumTab>(
    "tab",
    `/forums/${id}/threads`,
    "threads",
    FORUM_TABS
  );
  const [activityWindow, setActivityWindow] = useState<"24h" | "7d">("24h");

  // Lightweight forum context (title, price…) reused from the cached forums list.
  const { data: forums = [] } = useQuery({
    queryKey: ["forums"],
    queryFn: () => api.forums(),
    staleTime: 60_000,
  });
  const forum: Forum | undefined = forums.find((f) => String(f.id) === String(id));

  // Meta description enriched with the forum's live numbers so every page is
  // unique and data-rich (long-tail SEO), instead of one boilerplate sentence.
  useSeo({
    title: forum?.title
      ? `Forum ${forum.title} — discussions & cours`
      : "Forum Boursorama — discussions & cours",
    description: forum?.title
      ? [
          `Le forum ${forum.title} sur Boursorama en temps réel`,
          `${formatNumber(forum.posts_7d)} messages et ${formatNumber(forum.threads_7d)} sujets sur 7 jours`,
          forum.last_price != null
            ? `cours à ${formatNumber(forum.last_price)} ${forum.price_currency ?? "€"}${
                forum.last_variation_pct != null
                  ? ` (${forum.last_variation_pct > 0 ? "+" : ""}${forum.last_variation_pct.toLocaleString("fr-FR")} %)`
                  : ""
              }`
            : null,
        ]
          .filter(Boolean)
          .join(" : ") + ". Actualités, historique et alertes."
      : "Discussions, actualités et historique de cours d'une valeur sur les forums Boursorama.",
    // Canonical → the server-rendered fiche /valeurs/:slug : same forum, but
    // that page is crawlable without JS and is the one listed in the sitemap.
    // Avoids Google picking its own canonical between the two URLs.
    canonicalPath: forum?.slug ? `/valeurs/${forum.slug}` : `/forums/${id}/threads`,
  });

  // Counts displayed in the tab labels — same queries that the inner
  // components use, so React Query dedupes them.
  const { data: threads = [] } = useQuery({
    queryKey: ["threads", id],
    queryFn: () => api.threads(id!),
    enabled: !!id,
  });
  const { data: news = [] } = useQuery({
    queryKey: ["forum-news", id],
    queryFn: () => api.forumNews(id!),
    enabled: !!id,
  });
  const { data: history = [] } = useQuery({
    queryKey: ["forum-history", id],
    queryFn: () => api.forumHistory(id!),
    enabled: !!id,
  });

  const win = activityWindow === "24h"
    ? {
        label: "24 h",
        threads: forum?.threads_24h ?? 0,
        threadsPrev: forum?.threads_24h_prev ?? 0,
        posts: forum?.posts_24h ?? 0,
        postsPrev: forum?.posts_24h_prev ?? 0,
      }
    : {
        label: "7 j",
        threads: forum?.threads_7d ?? 0,
        threadsPrev: forum?.threads_7d_prev ?? 0,
        posts: forum?.posts_7d ?? 0,
        postsPrev: forum?.posts_7d_prev ?? 0,
      };

  return (
    <div className="flex flex-col gap-4">
      {/* ── Instrument header ── title + live cotation as the hero, a quiet
          identity line (ISIN / slug / Boursorama), then a compact stat strip
          that reads like a trading-desk instrument card. ── */}
      <header className="flex flex-col gap-3 rounded-xl border border-border bg-surface p-4 sm:p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex flex-col gap-1.5 min-w-0 flex-1">
            <div className="flex items-center gap-2.5 flex-wrap">
              <h1 className="text-2xl font-semibold leading-tight">{forum?.title ?? "Forum"}</h1>
              {forum && <CategoryBadge category={forum.category ?? "unknown"} />}
            </div>
            {/* Identity line — quiet, monospaced data tokens. */}
            <div className="flex items-center gap-x-2 gap-y-1 text-xs text-foreground-muted flex-wrap">
              {forum?.isin && <span className="font-mono">{forum.isin}</span>}
              {forum?.symbol && (
                <>
                  <span aria-hidden className="opacity-50">·</span>
                  <span className="font-mono">{forum.symbol}</span>
                </>
              )}
              {forum?.slug && (
                <>
                  <span aria-hidden className="opacity-50">·</span>
                  <span className="font-mono opacity-80">{forum.slug}</span>
                </>
              )}
              {forum?.url && (
                <>
                  <span aria-hidden className="opacity-50">·</span>
                  <a
                    href={forum.url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-primary hover:underline inline-flex items-center gap-1"
                  >
                    Boursorama <ArrowUpRight className="size-3" />
                  </a>
                </>
              )}
            </div>
          </div>
          <div className="flex items-start gap-3 sm:shrink-0">
            {forum && (
              <PriceTag
                size="md"
                price={forum.last_price}
                variation={forum.last_variation_pct}
                currency={forum.price_currency}
                fetchedAt={forum.last_price_at}
                forumId={forum.id}
              />
            )}
            {id && <RefreshAllButton forumId={id} />}
          </div>
        </div>

        {/* ── Stat strip ── score + windowed activity + last reply, each with a
            tooltip explaining what the number means. Window toggle lets the
            reader switch the activity lens (24 h / 7 j). ── */}
        {forum && (
          <div className="flex items-stretch gap-3 flex-wrap border-t border-border pt-3">
            <HeaderStat
              label="Score d'activité"
              hint="Indice de vivacité du forum (0–100), calculé sur le volume et la fraîcheur des messages. 100 = très actif, 0 = mort."
              value={formatScore(forum.active_score)}
            />
            <div className="w-px self-stretch bg-border" aria-hidden />
            <HeaderStat
              label={`Nouveaux sujets · ${win.label}`}
              hint={`Sujets créés sur les dernières ${win.label}, comparés à la période précédente de même durée.`}
              value={formatNumber(win.threads)}
              trend={<TrendBadge current={win.threads} previous={win.threadsPrev} />}
            />
            <HeaderStat
              label={`Messages · ${win.label}`}
              hint={`Messages postés sur les dernières ${win.label}, comparés à la période précédente de même durée.`}
              value={formatNumber(win.posts)}
              trend={<TrendBadge current={win.posts} previous={win.postsPrev} />}
            />
            <div className="w-px self-stretch bg-border" aria-hidden />
            <HeaderStat
              label="Dernier message"
              hint="Date du message le plus récent observé sur ce forum."
              value={
                <span className="inline-flex items-center gap-1 text-base font-medium">
                  <Clock className="size-3.5 text-foreground-muted" />
                  {fromNowFR(forum.last_reply_at)}
                </span>
              }
            />
            {/* Window toggle, pushed to the end. */}
            <div className="ml-auto self-center inline-flex rounded-md border border-border p-0.5 text-xs">
              {(["24h", "7d"] as const).map((w) => (
                <button
                  key={w}
                  type="button"
                  onClick={() => setActivityWindow(w)}
                  className={cn(
                    "rounded px-2 py-1 font-medium transition-colors",
                    activityWindow === w
                      ? "bg-primary text-primary-foreground"
                      : "text-foreground-muted hover:bg-surface-muted"
                  )}
                >
                  {w === "24h" ? "24 h" : "7 j"}
                </button>
              ))}
            </div>
          </div>
        )}
      </header>

      {id && <SummarizeButton kind="forum" id={id} label="Analyser (news + cours)" />}

      {id && (
        <Tabs value={tab} onValueChange={(v) => setTab(v as ForumTab)}>
          <TabsList>
            <TabsTrigger value="threads">
              <Hash className="size-3.5" />
              Threads
              <span className="ml-1 text-xs text-foreground-muted">
                ({formatNumber(threads.length)})
              </span>
            </TabsTrigger>
            <TabsTrigger value="news">
              <Newspaper className="size-3.5" />
              Actualités
              <span className="ml-1 text-xs text-foreground-muted">
                ({formatNumber(news.length)})
              </span>
            </TabsTrigger>
            <TabsTrigger value="history">
              <CalendarRange className="size-3.5" />
              Historique
              <span className="ml-1 text-xs text-foreground-muted">
                ({formatNumber(history.length)})
              </span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="threads">
            <ForumThreadsTable forumId={id} />
          </TabsContent>
          <TabsContent value="news">
            <ForumNews forumId={id} />
          </TabsContent>
          <TabsContent value="history">
            <ForumHistory forumId={id} currency={forum?.price_currency} />
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}

/**
 * HeaderStat — one cell of the instrument header's stat strip. A small
 * uppercase label (with a help tooltip), a large tabular value, and an
 * optional trend pill beneath it.
 */
function HeaderStat({
  label,
  value,
  hint,
  trend,
}: {
  label: string;
  value: React.ReactNode;
  hint: string;
  trend?: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-0.5 min-w-[5.5rem]">
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="w-fit cursor-help text-[10px] font-medium uppercase tracking-wide text-foreground-muted decoration-dotted underline-offset-2 hover:underline">
            {label}
          </span>
        </TooltipTrigger>
        <TooltipContent className="max-w-[16rem]">{hint}</TooltipContent>
      </Tooltip>
      <div className="flex items-center gap-2">
        <span className="text-lg font-semibold tabular-nums leading-none">{value}</span>
        {trend}
      </div>
    </div>
  );
}
